import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Play, Check, Star, MessageCircle, ChevronLeft, ChevronRight, ChevronDown, Clock, Activity, ShieldCheck, Zap, Flame, ThumbsUp, Battery, Eye
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { onMaxUpdate, emitMaxUpdate } from "./syncEvents";
import { WorkoutTimer } from "./WorkoutTimer";
import { ExerciseSetTracker } from "./ExerciseSetTracker";
import { GuidedWorkoutMode } from "./GuidedWorkoutMode";
import { FocusedWorkoutOverlay } from "./FocusedWorkoutOverlay";
import WorkoutCompletionCelebration from "./WorkoutCompletionCelebration";
import { MidWorkoutCheck } from "./ReadinessCheckIn";

const WorkoutDetailModal = ({ workout, onClose, onStart, programContext = null }) => {
  const STORAGE_KEY = `paladin_workout_${workout?.workout_id || "unknown"}`;

  // Restore from localStorage if available
  const getStored = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const stored = JSON.parse(raw);
      // Expire after 24 hours
      if (stored._timestamp && Date.now() - stored._timestamp > 24 * 60 * 60 * 1000) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return stored;
    } catch { return null; }
  };
  const stored = getStored();

  const [isStarted, setIsStarted] = useState(stored?.isStarted || false);
  const [guidedMode, setGuidedMode] = useState(stored?.guidedMode || false);
  const [focusOverlayOpen, setFocusOverlayOpen] = useState(false);
  const [startTime, setStartTime] = useState(stored?.startTime || null);
  const [exerciseLogs, setExerciseLogs] = useState(stored?.exerciseLogs || {});
  const [rating, setRating] = useState(0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [celebrationData, setCelebrationData] = useState(null);
  const [userMaxes, setUserMaxes] = useState(stored?.userMaxes || {});
  const [equipmentList, setEquipmentList] = useState([]);
  const [slotLevels, setSlotLevels] = useState({});
  const [selectedLevels, setSelectedLevels] = useState(stored?.selectedLevels || {});
  const [weightIntents, setWeightIntents] = useState(stored?.weightIntents || {});
  const modalContentRef = useRef(null);  // Ref for scrolling to top
  const exerciseRefs = useRef({});  // Refs for each exercise tracker
  
  // Readiness check-in state (strength workouts only)
  const isStrength = (workout.category || "").toLowerCase() === "strength";
  const [preWorkoutMood, setPreWorkoutMood] = useState(stored?.preWorkoutMood || null);
  const [isDeloadMode, setIsDeloadMode] = useState(stored?.isDeloadMode || false);
  const [deloadSwitchPoint, setDeloadSwitchPoint] = useState(null);
  const [dismissedMidChecks, setDismissedMidChecks] = useState({});
  const [safetyConfirmed, setSafetyConfirmed] = useState(stored?.safetyConfirmed || false);
  const [exercisePrefs, setExercisePrefs] = useState({});
  const [bodyweight, setBodyweight] = useState(null);
  const [showModeSelect, setShowModeSelect] = useState(false);
  const [activeBlockIndex, setActiveBlockIndex] = useState(stored?.activeBlockIndex || 0);
  const [acknowledgedNotes, setAcknowledgedNotes] = useState(stored?.acknowledgedNotes || {});
  const [showPreview, setShowPreview] = useState(false);
  const [workoutPhase, setWorkoutPhase] = useState(stored?.workoutPhase || "pre");
  const [pendingMaxes, setPendingMaxes] = useState({});
  const [videoMap, setVideoMap] = useState({});
  const [showResumePrompt, setShowResumePrompt] = useState(!!stored?.isStarted);

  // Save workout state to localStorage on every meaningful change
  useEffect(() => {
    if (!isStarted) return;
    const state = {
      isStarted, guidedMode, startTime, exerciseLogs, userMaxes, selectedLevels,
      weightIntents, preWorkoutMood, isDeloadMode, safetyConfirmed, activeBlockIndex,
      acknowledgedNotes, workoutPhase, _timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [isStarted, guidedMode, startTime, exerciseLogs, userMaxes, selectedLevels, weightIntents, preWorkoutMood, isDeloadMode, safetyConfirmed, activeBlockIndex, acknowledgedNotes, workoutPhase, STORAGE_KEY]);

  // Clear stored state when workout is completed or cancelled
  const clearStoredWorkout = () => { localStorage.removeItem(STORAGE_KEY); };

  const handleDiscardResume = () => {
    clearStoredWorkout();
    setShowResumePrompt(false);
    setIsStarted(false);
    setWorkoutPhase("pre");
    setExerciseLogs({});
    setStartTime(null);
    setActiveBlockIndex(0);
    setSafetyConfirmed(false);
    setPreWorkoutMood(null);
    setIsDeloadMode(false);
    setAcknowledgedNotes({});
  };
  
  // Fetch user maxes, equipment, weight intents, and progression levels on mount
  useEffect(() => {
    fetchUserMaxes();
    fetchEquipment();
    fetchWeightIntents();
    fetchExercisePrefs();
    fetchBodyweight();
    fetchVideoMap();
    if (workout?.workout_id) fetchSlotLevels();
    // Listen for max updates from other components (e.g., Guided Mode)
    const unsub = onMaxUpdate((exerciseId, maxWeight) => {
      setUserMaxes(prev => ({ ...prev, [exerciseId]: maxWeight }));
    });
    return unsub;
  }, []);

  // Auto-scroll to top on phase or block change
  useEffect(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [workoutPhase, activeBlockIndex]);

  
  const fetchUserMaxes = async () => {
    try {
      const response = await fetch(`${API}/workouts/maxes`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        const maxMap = {};
        (data.maxes || []).forEach(m => {
          maxMap[m.exercise_id] = m.max_weight;
        });
        setUserMaxes(maxMap);
      }
    } catch (error) {
      console.error("Failed to fetch user maxes:", error);
    }
  };

  const fetchVideoMap = async () => {
    try {
      const res = await fetch(`${API}/videos/exercise-map`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setVideoMap(data.map || {});
      }
    } catch {}
  };


  const fetchExercisePrefs = async () => {
    try {
      const res = await fetch(`${API}/workouts/exercise-preferences`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        const prefMap = {};
        (data.preferences || []).forEach(p => { prefMap[p.exercise_id] = p.preferred_id; });
        setExercisePrefs(prefMap);
      }
    } catch {}
  };

  const fetchBodyweight = async () => {
    try {
      const res = await fetch(`${API}/progression/bodyweight`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setBodyweight(data.bodyweight || null);
      }
    } catch {}
  };

  // Compute progression info for a given section's primary exercise
  const getProgressionInfo = (slotName) => {
    if (!slotName || !workout.progression_slots) return null;
    const slot = workout.progression_slots.find(ps => ps.name === slotName);
    if (!slot || !slot.levels || slot.levels.length <= 1) return null;
    const slotInfo = slotLevels[slotName];
    const currentLevel = slotInfo?.current_level || 1;
    const totalLevels = slot.levels.length;
    const currentLevelConfig = slot.levels.find(lv => lv.level === currentLevel);
    const nextLevelConfig = slot.levels.find(lv => lv.level === currentLevel + 1);
    if (!currentLevelConfig) return null;

    const advType = currentLevelConfig.advancement_type || "bw_multiplier";
    const advValue = parseFloat(currentLevelConfig.advancement_value || currentLevelConfig.bw_multiplier || 0);
    const rmType = currentLevelConfig.rm_type || "";

    let targetWeight = null;
    if (advType === "absolute_weight") {
      targetWeight = advValue;
    } else if (advType === "bw_multiplier" && bodyweight) {
      targetWeight = Math.round(bodyweight * advValue * 10) / 10;
    } else if (advType === "speed") {
      targetWeight = advValue;
    }

    const exerciseId = currentLevelConfig.exercise_id;
    const currentMax = userMaxes[exerciseId] || 0;
    let progressPercent = 0;
    if (targetWeight && targetWeight > 0) {
      progressPercent = Math.min(100, Math.round((currentMax / targetWeight) * 100));
    }

    const criteriaMet = targetWeight !== null && (
      advType === "speed" ? currentMax > 0 && currentMax <= targetWeight : currentMax >= targetWeight
    );

    return {
      slotName,
      workoutId: workout.workout_id,
      currentLevel,
      totalLevels,
      isMaxLevel: !nextLevelConfig,
      advancementType: advType,
      advancementValue: advValue,
      rmType,
      targetWeight,
      currentMax,
      progressPercent,
      criteriaMet: criteriaMet && !!nextLevelConfig,
      currentExerciseName: currentLevelConfig.exercise_name,
      nextLevelExerciseName: nextLevelConfig?.exercise_name || null,
      bodyweight,
    };
  };

  const handleLevelUp = async (slotName) => {
    try {
      const res = await fetch(`${API}/progression/accept-swap`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ workout_id: workout.workout_id, slot_name: slotName }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || "Leveled up!");
        // Refresh slot levels to reflect the new level
        fetchSlotLevels();
      } else {
        toast.error("Failed to level up");
      }
    } catch {
      toast.error("Something went wrong");
    }
  };

  
  const fetchEquipment = async () => {
    try {
      const response = await fetch(`${API}/workouts/equipment`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setEquipmentList(data.equipment || []);
      }
    } catch (error) {
      console.error("Failed to fetch equipment:", error);
    }
  };

  const fetchWeightIntents = async () => {
    try {
      const response = await fetch(`${API}/workouts/weight-intents`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        const intentMap = {};
        (data.intents || []).forEach(i => { intentMap[i.exercise_id] = i.intent; });
        setWeightIntents(intentMap);
      }
    } catch (error) {
      console.error("Failed to fetch weight intents:", error);
    }
  };

  const saveWeightIntent = async (exerciseId, intent) => {
    setWeightIntents(prev => ({ ...prev, [exerciseId]: intent }));
    try {
      await fetch(`${API}/workouts/weight-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ exercise_id: exerciseId, intent })
      });
    } catch (error) {
      console.error("Failed to save weight intent:", error);
    }
  };

  const fetchSlotLevels = async () => {
    try {
      const response = await fetch(`${API}/progression/workout-levels/${workout.workout_id}`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        const slots = data.slots || {};
        setSlotLevels(slots);
        // Initialize selected levels from the athlete's saved selections
        const initial = {};
        Object.entries(slots).forEach(([name, info]) => {
          initial[name] = info.selected_level || info.current_level || 1;
        });
        setSelectedLevels(initial);
      }
    } catch (error) {
      console.error("Failed to fetch slot levels:", error);
    }
  };

  const handleLevelChange = async (slotName, level) => {
    setSelectedLevels(prev => ({ ...prev, [slotName]: level }));
    // Persist the selection
    try {
      await fetch(`${API}/progression/select-level`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ workout_id: workout.workout_id, slot_name: slotName, selected_level: level })
      });
    } catch (error) {
      console.error("Failed to save level selection:", error);
    }
  };

  // Resolve a section's exercise based on progression slot level
  const resolveSection = (section) => {
    if (!workout.progression_slots || !workout.progression_slots.length) return section;
    // Match section name to progression slot name (section name is "Block X", slot name is "X")
    const sectionLetter = section.name?.replace(/^Block\s*/i, "").trim();
    const matchedSlot = workout.progression_slots.find(ps => ps.name === sectionLetter);
    if (!matchedSlot) return section;
    
    const selectedLevel = selectedLevels[matchedSlot.name] || 1;
    const levelConfig = matchedSlot.levels.find(lv => lv.level === selectedLevel);
    if (!levelConfig) return section;
    
    // Replace the exercise with the selected level's exercise
    const resolvedExercises = section.exercises?.map((ex, i) => {
      if (i === 0) { // First exercise in the section corresponds to the progression slot
        return {
          ...ex,
          exercise_id: levelConfig.exercise_id,
          exercise_name: levelConfig.exercise_name,
          bar_equipment_id: levelConfig.bar_equipment_id || ex.bar_equipment_id,
          sets_data: levelConfig.sets?.map((s, si) => ({
            set_num: si + 1,
            percent_max: s.percent || "100",
            reps: s.reps || "",
            rest_mins: s.rest_mins || "3"
          })) || ex.sets_data
        };
      }
      return ex;
    }) || [];
    
    return { ...section, exercises: resolvedExercises, _slotName: matchedSlot.name };
  };
  
  // Save a single max
  const saveUserMax = async (exerciseId, exerciseName, maxWeight) => {
    try {
      await fetch(`${API}/workouts/maxes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          exercise_id: exerciseId,
          exercise_name: exerciseName,
          max_weight: maxWeight
        })
      });
      setUserMaxes(prev => ({ ...prev, [exerciseId]: maxWeight }));
      emitMaxUpdate(exerciseId, maxWeight);
    } catch (error) {
      console.error("Failed to save max:", error);
    }
  };

  const handleStartWorkout = () => {
    setSafetyConfirmed(true);
    // Skip maxes screen — go straight to loadup
    setWorkoutPhase("loadup");
    setTimeout(() => {
      if (modalContentRef.current) {
        modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleMaxesConfirmed = () => {
    // Save all pending maxes
    Object.entries(pendingMaxes).forEach(([exId, val]) => {
      if (val && val !== userMaxes[exId]) {
        const exName = getAllExercises().find(e => e.exercise_id === exId)?.exercise_name || "";
        saveUserMax(exId, exName, parseFloat(val));
      }
    });
    setWorkoutPhase("loadup");
    setTimeout(() => {
      if (modalContentRef.current) {
        modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleBeginWorkout = () => {
    setIsStarted(true);
    setStartTime(Date.now());
    setWorkoutPhase("active");
    setFocusOverlayOpen(true);
    toast.success("Let's go!");
    setTimeout(() => {
      if (modalContentRef.current) {
        modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  // Auto-scroll to next exercise when one completes
  const handleExerciseComplete = (sectionIndex, exerciseIndex) => {
    const section = resolveSection((workout.sections || [])[sectionIndex]);
    const exercises = section?.exercises || [];
    const nextExIndex = exerciseIndex + 1;

    if (nextExIndex < exercises.length) {
      // Next exercise in same block
      const refKey = `${sectionIndex}-${nextExIndex}`;
      setTimeout(() => {
        const el = exerciseRefs.current[refKey];
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    } else {
      // All exercises in this block done — check for next block
      const sections = workout.sections || [];
      if (sectionIndex + 1 < sections.length) {
        // Move to next block in guided mode
        const nextBlock = sectionIndex + 1;
        if (activeBlockIndex !== undefined) {
          setActiveBlockIndex(nextBlock);
          setTimeout(() => {
            if (modalContentRef.current) modalContentRef.current.scrollTo({ top: 0, behavior: "smooth" });
          }, 200);
        }
      }
    }
  };

  // Get flat list of all exercises across sections (resolved)
  const getAllExercises = () => {
    const exercises = [];
    (workout.sections || []).forEach((rawSection, si) => {
      const section = resolveSection(rawSection);
      (section.exercises || []).forEach((ex, ei) => {
        exercises.push({ ...ex, _sectionIndex: si, _exerciseIndex: ei, _sectionName: rawSection.name || `Block ${String.fromCharCode(65 + si)}` });
      });
    });
    return exercises;
  };

  // Readiness check-in handlers
  const handleMoodSelect = (mood) => {
    setPreWorkoutMood(mood);
    if (mood === "not_great") {
      setIsDeloadMode(true);
      setDeloadSwitchPoint("Pre-workout");
    }
  };

  const handleMidWorkoutDeload = (blockName, setNum) => {
    setIsDeloadMode(true);
    setDeloadSwitchPoint(`${blockName}, Set ${setNum}`);
    toast("Switching to deload", { description: "Remaining reps halved" });
  };

  const handleDismissMidCheck = (key) => {
    setDismissedMidChecks(prev => ({ ...prev, [key]: true }));
  };

  const handleStartGuidedMode = () => {
    setGuidedMode(true);
  };

  // If guided mode is active, render that instead
  if (guidedMode) {
    const resolvedWorkout = { ...workout, sections: (workout.sections || []).map(s => resolveSection(s)) };
    return (
      <GuidedWorkoutMode
        workout={resolvedWorkout}
        userMaxes={userMaxes}
        equipmentList={equipmentList}
        programContext={programContext}
        preWorkoutMood={preWorkoutMood}
        isDeloadMode={isDeloadMode}
        onComplete={onStart}
        onClose={() => {
          setGuidedMode(false);
          onClose();
        }}
      />
    );
  }

  const updateExerciseLog = (sectionIndex, exerciseIndex, logData) => {
    const key = `${sectionIndex}-${exerciseIndex}`;
    setExerciseLogs(prev => ({
      ...prev,
      [key]: logData
    }));
  };

  // Calculate completion stats
  const getTotalExercises = () => {
    if (!workout.sections) return 0;
    return workout.sections.reduce((acc, section) => acc + (section.exercises?.length || 0), 0);
  };

  const getCompletedExercises = () => {
    return Object.values(exerciseLogs).filter(log => log.completed).length;
  };

  const handleCompleteWorkout = async () => {
    setSaving(true);
    const durationMinutes = startTime ? Math.round((Date.now() - startTime) / 60000) : 0;

    // Build exercise log array for backend
    const exerciseLogArray = Object.entries(exerciseLogs).map(([key, log]) => ({
      exercise_id: log.exercise_id,
      exercise_name: log.exercise_name,
      sets: log.sets.map(s => ({
        weight: s.weight ? parseFloat(s.weight) : null,
        reps: s.reps ? parseInt(s.reps) : null,
        completed: s.completed
      }))
    }));

    try {
      const response = await fetch(`${API}/workouts/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          workout_id: workout.workout_id,
          duration_minutes: durationMinutes || workout.estimated_duration || 45,
          rating: rating || null,
          notes: notes || null,
          program_id: programContext?.program_id || null,
          week_number: programContext?.week || null,
          day_number: programContext?.day || null,
          exercise_logs: exerciseLogArray,
          pre_workout_mood: preWorkoutMood || null,
          switched_to_deload: isDeloadMode,
          deload_switch_point: deloadSwitchPoint || null,
        })
      });

      if (response.ok) {
        const result = await response.json();
        clearStoredWorkout();
        setCelebrationData(result);
        // Trigger refresh in background
        if (onStart) onStart();
      } else {
        toast.error("Failed to save completion");
      }
    } catch (error) {
      console.error("Error completing workout:", error);
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const totalExercises = getTotalExercises();
  const completedExercises = getCompletedExercises();
  const progressPercent = totalExercises > 0 ? Math.round((completedExercises / totalExercises) * 100) : 0;

  return (
    <>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-surface border border-border rounded-2xl w-full max-w-3xl h-[90vh] flex flex-col overflow-x-hidden"
        data-testid="workout-detail-modal"
      >
        {/* Header - Fixed at top */}
        <div className="flex-shrink-0 bg-surface border-b border-border p-3 sm:p-4 md:p-6 flex justify-between items-start gap-2 rounded-t-2xl">
          <div className="min-w-0 flex-1">
            <h2 className="font-heading text-lg sm:text-xl md:text-2xl font-bold break-words">{workout.title}</h2>
            <div className="flex flex-wrap gap-1 sm:gap-1.5 md:gap-2 mt-1.5 sm:mt-2">
              <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded capitalize">
                {workout.category || "strength"}
              </span>
              <span className="text-xs bg-[#2D3B4F] text-text-secondary px-2 py-1 rounded">
                {workout.times_per_week ? `${workout.times_per_week}x/wk` : workout.difficulty || ""}
              </span>
              <span className="text-xs bg-accent/20 text-accent px-2 py-1 rounded">
                {workout.estimated_duration || 45} min
              </span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#1E293B] rounded-lg flex-shrink-0" data-testid="close-workout-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Fixed Timer & Progress Section (shown when workout is started) */}
        {isStarted && (
          <div className="flex-shrink-0 bg-surface border-b border-border px-3 sm:px-4 md:px-6 pb-3 sm:pb-4">
            {/* Workout Timer */}
            <WorkoutTimer startTime={startTime} isRunning={isStarted} />

            {/* Progress Bar */}
            <div className="mt-3 sm:mt-4">
              <div className="flex justify-between text-xs sm:text-sm mb-1.5 sm:mb-2">
                <span className="text-text-secondary">Exercise Progress</span>
                <span className="text-primary font-medium">{completedExercises}/{totalExercises} completed</span>
              </div>
              <div className="w-full bg-[#2D3B4F] rounded-full h-1.5 sm:h-2">
                <div 
                  className="bg-primary h-1.5 sm:h-2 rounded-full transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            {/* Resume Focus pill — only when overlay was closed mid-workout */}
            {!focusOverlayOpen && completedExercises < totalExercises && (
              <button
                onClick={() => setFocusOverlayOpen(true)}
                className="mt-3 w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold py-2.5 rounded-xl text-sm transition-colors"
                data-testid="resume-focus-btn">
                <Play className="w-4 h-4" /> Resume Focus Mode
              </button>
            )}
          </div>
        )}

        {/* Scrollable Content Area */}
        <div ref={modalContentRef} className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6">

          {/* Resume Prompt — shown when returning to an in-progress workout */}
          {showResumePrompt && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
              className="mb-4 bg-blue-500/10 border border-blue-500/30 rounded-xl p-5" data-testid="resume-prompt">
              <h3 className="text-base font-bold text-blue-400 mb-2">Workout in Progress</h3>
              <p className="text-sm text-white/60 mb-4">
                Looks like you have an active workout. Pick up where you left off?
              </p>
              <div className="flex gap-3">
                <button onClick={() => setShowResumePrompt(false)}
                  className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 rounded-xl transition-colors text-sm"
                  data-testid="resume-workout-btn">
                  Resume Workout
                </button>
                <button onClick={handleDiscardResume}
                  className="flex-1 bg-[#1E293B] hover:bg-[#2D3B4F] text-white/60 font-semibold py-3 rounded-xl transition-colors text-sm"
                  data-testid="start-fresh-btn">
                  Start Fresh
                </button>
              </div>
            </motion.div>
          )}

          {workoutPhase === "pre" ? (
            /* ========== COMBINED PRE-WORKOUT VIEW ========== */
            <div className="space-y-4">

              {/* Inline Readiness Check-In (strength workouts only) */}
              {isStrength && (
                <div data-testid="readiness-checkin">
                  <p className="text-sm font-semibold text-gray-300 mb-2">How are you feeling? <span className="text-gray-500 font-normal">(Pick one)</span></p>
                  <div className="flex gap-2">
                    {[
                      { id: "ready", label: "Ready", icon: Flame, color: "emerald" },
                      { id: "alright", label: "Alright", icon: ThumbsUp, color: "blue" },
                      { id: "not_great", label: "Not Great", icon: Battery, color: "amber" },
                    ].map((mood) => {
                      const Icon = mood.icon;
                      const isSelected = preWorkoutMood === mood.id;
                      const colorMap = {
                        emerald: { bg: "bg-emerald-500/15", border: "border-emerald-500/50", text: "text-emerald-400", iconBg: "bg-emerald-500" },
                        blue: { bg: "bg-blue-500/15", border: "border-blue-500/50", text: "text-blue-400", iconBg: "bg-blue-500" },
                        amber: { bg: "bg-amber-500/15", border: "border-amber-500/50", text: "text-amber-400", iconBg: "bg-amber-500" },
                      };
                      const c = colorMap[mood.color];
                      return (
                        <button
                          key={mood.id}
                          type="button"
                          onClick={() => handleMoodSelect(mood.id)}
                          className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 transition-all ${
                            isSelected ? `${c.bg} ${c.border}` : "border-[#1E293B] hover:border-[#334155]"
                          }`}
                          data-testid={`mood-${mood.id}`}
                        >
                          <div className={`w-7 h-7 rounded-lg ${isSelected ? c.iconBg : "bg-[#2D3B4F]"} flex items-center justify-center flex-shrink-0`}>
                            <Icon className="w-3.5 h-3.5 text-white" />
                          </div>
                          <span className={`text-xs font-semibold ${isSelected ? c.text : "text-gray-400"}`}>{mood.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  {/* Deload indicator */}
                  {isDeloadMode && (
                    <div className="mt-2 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30">
                      <Battery className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-xs text-amber-400 font-medium">Deload mode — reps will be halved</span>
                      <button type="button" onClick={() => { setIsDeloadMode(false); setDeloadSwitchPoint(null); }} className="ml-auto text-xs text-amber-400/60 hover:text-amber-400">Undo</button>
                    </div>
                  )}
                </div>
              )}

              {/* Safety Acknowledgment */}
              {isStrength && (
                <div
                  className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${
                    safetyConfirmed
                      ? "bg-emerald-500/10 border-emerald-500/30"
                      : "bg-amber-500/10 border-amber-500/40"
                  }`}
                  data-testid="workout-safety-gate"
                >
                  <button
                    type="button"
                    onClick={() => setSafetyConfirmed(!safetyConfirmed)}
                    className={`mt-0.5 w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border-2 transition-colors ${
                      safetyConfirmed
                        ? "bg-emerald-500 border-emerald-500"
                        : "border-amber-400 hover:bg-amber-400/20"
                    }`}
                    data-testid="workout-safety-checkbox"
                  >
                    {safetyConfirmed && <Check className="w-3 h-3 text-white" />}
                  </button>
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${safetyConfirmed ? "text-emerald-400" : "text-amber-400"}`}>
                      {safetyConfirmed ? "Safety confirmed" : "Load plates symmetrically — inner plates first, off last"}
                    </p>
                  </div>
                </div>
              )}

              {/* Description */}
              {workout.description && (
                <p className="text-text-secondary text-sm">{workout.description}</p>
              )}

              {/* Collapsible Workout Preview */}
              {workout.sections && workout.sections.length > 0 && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowPreview(!showPreview)}
                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors py-1"
                    data-testid="preview-workout-toggle"
                  >
                    <Eye className="w-4 h-4" />
                    <span className="font-medium">Preview Workout</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showPreview ? "rotate-180" : ""}`} />
                    <span className="text-xs text-gray-600">({workout.sections.length} blocks)</span>
                  </button>

                  <AnimatePresence>
                    {showPreview && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-3 pt-3">
                  {workout.sections.map((rawSection, sectionIndex) => {
                    const section = resolveSection(rawSection);
                    const slotName = section._slotName;
                    const slotInfo = slotName ? slotLevels[slotName] : null;
                    const hasMultipleLevels = slotInfo && slotInfo.available_levels && slotInfo.available_levels.length > 1;
                    const isSuperset = section.section_type === "circuit" && section.exercises?.length > 1;
                    const coachNote = rawSection.coach_note || "";
                    return (
                      <div key={sectionIndex} className={`rounded-xl overflow-hidden border border-[#1E293B] ${isSuperset ? "border-l-4 border-l-amber-500" : ""}`}>
                        <div className="flex items-center justify-between gap-2 px-3 py-2.5 bg-[#141B2D]">
                          <h4 className="font-medium text-primary flex items-center gap-2 text-sm">
                            <span className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold ${isSuperset ? "bg-amber-500/20 text-amber-400" : "bg-primary/20"}`}>
                              {String.fromCharCode(65 + sectionIndex)}
                            </span>
                            {isSuperset ? (
                              <span className="text-amber-400">{section.name || "Superset"}</span>
                            ) : (
                              section.name || `Block ${String.fromCharCode(65 + sectionIndex)}`
                            )}
                          </h4>
                          {hasMultipleLevels && (
                            <select
                              value={selectedLevels[slotName] || 1}
                              onChange={(e) => handleLevelChange(slotName, parseInt(e.target.value))}
                              className="bg-[#1E293B] border border-[#334155] text-white text-xs rounded-lg px-2 py-1 outline-none focus:border-emerald-500"
                              data-testid={`level-select-${slotName}`}
                            >
                              {slotInfo.available_levels.map((lv) => (
                                <option key={lv.level} value={lv.level}>
                                  {lv.name || `Level ${lv.level}`}{lv.level === slotInfo.current_level ? " (Current)" : ""} — {lv.exercise_name}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                        {coachNote && (
                          <div className="flex items-start gap-2 px-3 py-2 bg-amber-500/5 border-t border-amber-500/20">
                            <MessageCircle className="w-3.5 h-3.5 text-amber-400/70 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-400/70 leading-relaxed">{coachNote}</p>
                          </div>
                        )}
                        <div className="px-3 py-2 space-y-1.5">
                          {section.exercises?.map((exercise, exIndex) => (
                            <ExerciseSetTracker
                              key={`${sectionIndex}-${exercise.exercise_id}-${selectedLevels[slotName] || 1}`}
                              exercise={{
                                ...exercise,
                                _athleteUsesAlt: exercise.alt_exercise_id && exercisePrefs[exercise.exercise_id] === exercise.alt_exercise_id
                              }}
                              sectionIndex={sectionIndex}
                              exerciseIndex={exIndex}
                              exerciseLog={exerciseLogs[`${sectionIndex}-${exIndex}`]}
                              onUpdateLog={updateExerciseLog}
                              isStarted={false}
                              userMax={userMaxes[exercise.exercise_id]}
                              onUpdateMax={saveUserMax}
                              equipmentList={equipmentList}
                              weightIntent={weightIntents[exercise.exercise_id]}
                              onSaveWeightIntent={saveWeightIntent}
                              isDeloadMode={isDeloadMode}
                              safetyConfirmed={true}
                              allUserMaxes={userMaxes}
                              progressionInfo={exIndex === 0 ? getProgressionInfo(slotName) : null}
                              onLevelUp={handleLevelUp}
                              videoInfo={videoMap[exercise.exercise_id]}
                              isStrengthWorkout={isStrength}
                              onSwitchExercise={(origId, chosenId) => {
                                fetch(`${API}/workouts/exercise-preference`, {
                                  method: "POST", credentials: "include",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({ exercise_id: origId, preferred_id: chosenId })
                                }).catch(() => {});
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          ) : workoutPhase === "maxes" ? (
            /* ========== SET YOUR MAXES ========== */
            <div className="space-y-4">
              <div className="text-center mb-2">
                <h3 className="text-base font-bold text-white">Confirm Your Maxes</h3>
                <p className="text-xs text-gray-500 mt-1">Verify or update before we calculate your weights</p>
              </div>
              <div className="space-y-2">
                {(() => {
                  const allExercises = getAllExercises();
                  return allExercises.map((ex) => {
                    const basedOff = ex.based_off_exercise_id;
                    const isDerived = basedOff && basedOff !== ex.exercise_id;
                    const intent = weightIntents[ex.exercise_id];
                    const derivedMax = isDerived ? (pendingMaxes[basedOff] || userMaxes[basedOff] || "") : null;
                    const currentMax = isDerived ? derivedMax : (pendingMaxes[ex.exercise_id] ?? "");

                    return (
                      <div
                        key={`${ex._sectionIndex}-${ex.exercise_id}`}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${
                          isDerived ? "border-[#1E293B] bg-[#0F1420]" : "border-[#2D3B4F] bg-[#111827]"
                        }`}
                        data-testid={`max-row-${ex.exercise_id}`}
                      >
                        <span className="w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold bg-primary/20 text-primary flex-shrink-0">
                          {String.fromCharCode(65 + ex._sectionIndex)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white font-medium truncate">{ex.exercise_name || "Exercise"}</p>
                          {isDerived && (
                            <p className="text-[10px] text-cyan-400/60 truncate">Based off {ex.based_off_exercise_name}</p>
                          )}
                        </div>
                        {isDerived ? (
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className="text-sm text-cyan-400/80">{derivedMax || "—"}</span>
                            <span className="text-[10px] text-cyan-400/50">lbs</span>
                            <Check className="w-3.5 h-3.5 text-cyan-400/60" />
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <input
                              type="number"
                              value={currentMax}
                              onChange={(e) => setPendingMaxes(prev => ({ ...prev, [ex.exercise_id]: e.target.value }))}
                              placeholder="Max"
                              className="w-20 bg-[#0B0F1A] border border-amber-500/50 rounded-lg px-2.5 py-1.5 text-white text-sm text-center focus:border-amber-400 outline-none"
                              data-testid={`max-setup-${ex.exercise_id}`}
                            />
                            <span className="text-[10px] text-amber-400/60">lbs</span>
                            {intent !== undefined && intent !== null && intent !== 0 && (
                              <span className={`text-[10px] font-semibold whitespace-nowrap ${
                                intent > 0 ? "text-emerald-400" : "text-red-400"
                              }`}>
                                {intent > 0 ? "+" : ""}{intent}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          ) : workoutPhase === "loadup" ? (
            /* ========== LOAD UP SCREEN ========== */
            <div className="space-y-5">
              {(() => {
                // Show first block, first exercise, Set 1 details
                const firstSection = workout.sections?.[0];
                if (!firstSection) return null;
                const section = resolveSection(firstSection);
                const firstEx = section.exercises?.[0];
                if (!firstEx) return null;

                const basedOff = firstEx.based_off_exercise_id;
                const isDerived = basedOff && basedOff !== firstEx.exercise_id;
                const maxVal = isDerived
                  ? (pendingMaxes[basedOff] || userMaxes[basedOff])
                  : (pendingMaxes[firstEx.exercise_id] || userMaxes[firstEx.exercise_id]);
                const maxNum = parseFloat(maxVal) || 0;

                // Calculate Set 1 weight
                const setConfig = firstEx.sets_data?.[0];
                let set1Weight = null;
                if (setConfig?.percent_max && maxNum > 0) {
                  const pct = parseFloat(setConfig.percent_max.toString().replace("%", ""));
                  set1Weight = Math.round((maxNum * pct / 100) / 5) * 5;
                }

                // Equipment / bar weight
                const exerciseName = (firstEx.exercise_name || "").toLowerCase();
                const matchedEq = firstEx.bar_equipment_id
                  ? equipmentList.find(eq => eq.equipment_id === firstEx.bar_equipment_id)
                  : [...equipmentList].sort((a, b) => (b.name || "").length - (a.name || "").length)
                    .find(eq => eq.name && exerciseName.includes(eq.name.toLowerCase()));
                const bw = matchedEq?.weight || 0;

                // Plate breakdown
                let plateStr = null;
                if (set1Weight && bw) {
                  let perSide = (set1Weight - bw) / 2;
                  if (perSide > 0) {
                    perSide = Math.round(perSide / 2.5) * 2.5;
                    const plates = [];
                    let rem = perSide;
                    for (const pw of [45, 35, 25, 10, 5, 2.5]) {
                      const c = Math.floor(rem / pw);
                      if (c > 0) { plates.push(`${pw}s x${c}`); rem -= c * pw; }
                    }
                    plateStr = plates.join(", ");
                  }
                }

                const coachNote = firstSection.coach_note || "";

                return (
                  <div className="text-center space-y-5">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Your first exercise</p>
                      <h3 className="text-lg font-bold text-white">{firstEx.exercise_name}</h3>
                      <p className="text-sm text-gray-400 mt-2">
                        Set 1 of {firstEx.sets_data?.length || firstEx.sets || "?"}
                      </p>
                    </div>

                    {set1Weight && (
                      <div className="rounded-2xl bg-[#111827] border border-[#2D3B4F] p-5 space-y-3">
                        <p className="text-4xl font-black text-white">{set1Weight} <span className="text-lg text-gray-500">lbs</span> <span className="text-2xl text-gray-400 font-black">x {setConfig?.reps || firstEx.reps || "?"} reps</span></p>
                        {plateStr && bw > 0 && (
                          <div className="text-sm text-gray-400">
                            <span className="text-gray-600">Each side:</span> {plateStr}
                          </div>
                        )}
                        {bw > 0 && (
                          <p className="text-[10px] text-gray-600">Bar: {matchedEq?.name} ({bw} lbs)</p>
                        )}
                      </div>
                    )}

                    {coachNote && (
                      <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-500/5 border border-amber-500/20 text-left">
                        <MessageCircle className="w-4 h-4 text-amber-400/70 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-400/70 leading-relaxed">{coachNote}</p>
                      </div>
                    )}

                    <p className="text-sm text-gray-400">Load up your bar. When ready, tap below.</p>
                  </div>
                );
              })()}
            </div>
          ) : (
            /* ========== ACTIVE WORKOUT VIEW ========== */
            <>
              {/* Deload banner */}
              {isDeloadMode && (
                <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <Battery className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-xs font-bold text-amber-400 uppercase tracking-wider">Deload Active</span>
                  <span className="text-xs text-amber-400/60">Reps halved</span>
                </div>
              )}

              {/* Block-as-window */}
              {workout.sections && workout.sections.length > 0 && (() => {
                const totalBlocks = workout.sections.length;
                const rawSection = workout.sections[activeBlockIndex];
                if (!rawSection) return null;
                const section = resolveSection(rawSection);
                const slotName = section._slotName;
                const slotInfo = slotName ? slotLevels[slotName] : null;
                const hasMultipleLevels = slotInfo && slotInfo.available_levels && slotInfo.available_levels.length > 1;
                const isSuperset = section.section_type === "circuit" && section.exercises?.length > 1;
                const coachNote = rawSection.coach_note || "";

                const blockComplete = section.exercises?.every((_, exIndex) => {
                  const log = exerciseLogs[`${activeBlockIndex}-${exIndex}`];
                  return log && log.sets && log.sets.length > 0 && log.sets.every(s => s.completed);
                });

                return (
                  <div data-testid="active-block-view">
                    {/* Block Navigation Header */}
                    <div className="flex items-center justify-between mb-3">
                      <button
                        type="button"
                        onClick={() => setActiveBlockIndex(Math.max(0, activeBlockIndex - 1))}
                        disabled={activeBlockIndex === 0}
                        className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-[#1E293B] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        data-testid="prev-block-btn"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <div className="flex items-center gap-2">
                        {Array.from({ length: totalBlocks }).map((_, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setActiveBlockIndex(i)}
                            className={`w-2.5 h-2.5 rounded-full transition-all ${
                              i === activeBlockIndex ? "bg-primary w-6" :
                              i < activeBlockIndex ? "bg-emerald-500" : "bg-[#2D3B4F]"
                            }`}
                            data-testid={`block-dot-${i}`}
                          />
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setActiveBlockIndex(Math.min(totalBlocks - 1, activeBlockIndex + 1))}
                        disabled={activeBlockIndex === totalBlocks - 1}
                        className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-[#1E293B] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        data-testid="next-block-btn"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Coach note reminder */}
                    {coachNote && (
                      <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/20">
                        <MessageCircle className="w-3.5 h-3.5 text-amber-400/60" />
                        <p className="text-xs text-amber-400/60 truncate">{coachNote}</p>
                      </div>
                    )}

                    {/* Block content */}
                    <div className={`space-y-3 ${isSuperset ? "border-l-4 border-amber-500 pl-3 bg-amber-500/5 rounded-r-lg py-2" : ""}`}>
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="font-medium text-primary flex items-center gap-2">
                          <span className={`w-6 h-6 rounded flex items-center justify-center text-xs ${isSuperset ? "bg-amber-500/20 text-amber-400" : "bg-primary/20"}`}>
                            {String.fromCharCode(65 + activeBlockIndex)}
                          </span>
                          {isSuperset ? (
                            <span className="text-amber-400">{section.name || "Superset"} <span className="text-xs text-amber-400/60">({section.exercises?.length} exercises)</span></span>
                          ) : (
                            section.name || `Block ${String.fromCharCode(65 + activeBlockIndex)}`
                          )}
                          {blockComplete && (
                            <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full font-medium">Done</span>
                          )}
                        </h4>
                        {hasMultipleLevels && (
                          <select
                            value={selectedLevels[slotName] || 1}
                            onChange={(e) => handleLevelChange(slotName, parseInt(e.target.value))}
                            className="bg-[#1E293B] border border-[#334155] text-white text-xs rounded-lg px-2 py-1.5 outline-none focus:border-emerald-500 cursor-pointer"
                            data-testid={`level-select-${slotName}`}
                          >
                            {slotInfo.available_levels.map((lv) => (
                              <option key={lv.level} value={lv.level}>
                                {lv.name || `Level ${lv.level}`}{lv.level === slotInfo.current_level ? " (Current)" : ""} — {lv.exercise_name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      <div className="space-y-2">
                        {section.exercises?.map((exercise, exIndex) => (
                          <div key={`${activeBlockIndex}-${exercise.exercise_id}-${selectedLevels[slotName] || 1}`}
                            ref={el => { exerciseRefs.current[`${activeBlockIndex}-${exIndex}`] = el; }}>
                          <ExerciseSetTracker
                            exercise={{
                              ...exercise,
                              _athleteUsesAlt: exercise.alt_exercise_id && exercisePrefs[exercise.exercise_id] === exercise.alt_exercise_id
                            }}
                            sectionIndex={activeBlockIndex}
                            exerciseIndex={exIndex}
                            exerciseLog={exerciseLogs[`${activeBlockIndex}-${exIndex}`]}
                            onUpdateLog={updateExerciseLog}
                            isStarted={isStarted}
                            userMax={userMaxes[exercise.exercise_id]}
                            onUpdateMax={saveUserMax}
                            equipmentList={equipmentList}
                            weightIntent={weightIntents[exercise.exercise_id]}
                            onSaveWeightIntent={saveWeightIntent}
                            isDeloadMode={isDeloadMode}
                            safetyConfirmed={safetyConfirmed}
                            allUserMaxes={userMaxes}
                            progressionInfo={exIndex === 0 ? getProgressionInfo(slotName) : null}
                            onLevelUp={handleLevelUp}
                            videoInfo={videoMap[exercise.exercise_id]}
                            onExerciseComplete={handleExerciseComplete}
                            isStrengthWorkout={isStrength}
                            onSwitchExercise={(origId, chosenId) => {
                              fetch(`${API}/workouts/exercise-preference`, {
                                method: "POST", credentials: "include",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ exercise_id: origId, preferred_id: chosenId })
                              }).catch(() => {});
                            }}
                          />
                          </div>
                        ))}
                      </div>
                      {/* Mid-workout deload check */}
                      {isStrength && !isDeloadMode && (() => {
                        const blockKey = `block-${activeBlockIndex}`;
                        if (dismissedMidChecks[blockKey]) return null;
                        const log = exerciseLogs[`${activeBlockIndex}-0`];
                        if (!log || !log.sets) return null;
                        const totalSets = log.sets.length;
                        if (totalSets < 2) return null;
                        const completedCount = log.sets.filter(s => s.completed).length;
                        if (completedCount !== totalSets - 1) return null;
                        const blockName = section.name || `Block ${String.fromCharCode(65 + activeBlockIndex)}`;
                        return (
                          <MidWorkoutCheck
                            blockName={blockName}
                            setNum={completedCount}
                            onKeepPushing={() => handleDismissMidCheck(blockKey)}
                            onSwitchDeload={() => handleMidWorkoutDeload(blockName, completedCount)}
                          />
                        );
                      })()}

                      {/* Auto-advance */}
                      {blockComplete && activeBlockIndex < totalBlocks - 1 && (
                        <motion.button
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          type="button"
                          onClick={() => {
                            setActiveBlockIndex(activeBlockIndex + 1);
                            if (modalContentRef.current) {
                              modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                          }}
                          className="w-full mt-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors"
                          data-testid="next-block-auto-btn"
                        >
                          Next Block: {workout.sections[activeBlockIndex + 1]?.name || `Block ${String.fromCharCode(66 + activeBlockIndex)}`}
                          <ChevronRight className="w-4 h-4" />
                        </motion.button>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Completion Section */}
              <div className="mt-6 pt-6 border-t border-border space-y-4">
                <h3 className="font-semibold">Finish Your Workout</h3>
              
              {/* Rating */}
              <div>
                <label className="text-sm text-text-secondary mb-2 block">How was it?</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      className={`p-2 rounded-lg transition-colors ${
                        rating >= star ? "text-amber-400" : "text-[#475569] hover:text-zinc-400"
                      }`}
                      data-testid={`rating-star-${star}`}
                    >
                      <Star className={`w-6 h-6 ${rating >= star ? "fill-current" : ""}`} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-sm text-text-secondary mb-2 block">Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="How did it go? Any PRs? What felt hard?"
                  className="w-full bg-[#1E293B] border border-[#1E293B] rounded-lg px-4 py-3 text-white focus:border-primary outline-none resize-none"
                  rows={2}
                  data-testid="workout-notes-input"
                />
              </div>
            </div>
            </>
          )}
        </div>

        {/* Fixed Footer - Action Buttons */}
        <div className="flex-shrink-0 bg-surface border-t border-border p-3 sm:p-4 md:p-6 space-y-2 sm:space-y-3 rounded-b-2xl">
          {workoutPhase === "pre" ? (
            <button
              onClick={handleStartWorkout}
              disabled={isStrength && !safetyConfirmed}
              className="w-full bg-primary text-white py-3 sm:py-3.5 rounded-xl font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="start-workout-btn"
            >
              <Play className="w-4 h-4 sm:w-5 sm:h-5" />
              Start Workout
            </button>
          ) : workoutPhase === "maxes" ? (
            <button
              onClick={handleMaxesConfirmed}
              className="w-full bg-primary text-white py-3 sm:py-3.5 rounded-xl font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
              data-testid="maxes-confirmed-btn"
            >
              <Check className="w-4 h-4 sm:w-5 sm:h-5" />
              All Set — Load Up
            </button>
          ) : workoutPhase === "loadup" ? (
            <button
              onClick={handleBeginWorkout}
              className="w-full py-3.5 sm:py-4 rounded-xl font-bold text-sm sm:text-base text-white flex items-center justify-center gap-2 transition-all"
              style={{ background: "linear-gradient(135deg, #10B981 0%, #059669 100%)" }}
              data-testid="begin-workout-btn"
            >
              <Play className="w-5 h-5" />
              Start Timer & Begin
            </button>
          ) : (
            <button
              onClick={handleCompleteWorkout}
              disabled={saving}
              className="w-full bg-success text-white py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm sm:text-base"
              data-testid="complete-workout-btn"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-t-2 border-b-2 border-white"></div>
              ) : (
                <>
                  <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                  Complete Workout
                </>
              )}
            </button>
          )}
        </div>
      </motion.div>

      {/* Celebration Overlay */}
      {celebrationData && (
        <WorkoutCompletionCelebration
          data={celebrationData}
          workoutTitle={workout.title}
          onClose={() => { setCelebrationData(null); onClose(); }}
        />
      )}
    </motion.div>

    {/* Focused full-screen workout overlay (Variant A — full takeover).
        Opens when athlete taps "Begin Workout"; the modal stays mounted
        underneath as the read-only overview when the overlay is closed. */}
    <AnimatePresence>
      {focusOverlayOpen && isStarted && !celebrationData && (
        <FocusedWorkoutOverlay
          exercises={getAllExercises()}
          userMaxes={userMaxes}
          equipmentList={equipmentList}
          exerciseLogs={exerciseLogs}
          onUpdateLog={updateExerciseLog}
          onUpdateMax={saveUserMax}
          onSaveWeightIntent={saveWeightIntent}
          onWorkoutComplete={() => { setFocusOverlayOpen(false); handleCompleteWorkout(); }}
          onClose={() => setFocusOverlayOpen(false)}
          isStrengthWorkout={isStrength}
          isDeloadMode={isDeloadMode}
          videoMap={videoMap}
        />
      )}
    </AnimatePresence>
    </>
  );
};

export { WorkoutDetailModal };
